import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import Axios from 'axios';
import moment from 'moment';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import ToastrService from '@/shared/services/ToastrService';
import api from '@/shared/api';
import config from '@/config';
import intlShape from '@/shared/utils/intlShape';
import handleHttpError from '@/shared/services/HandleHttpError';
import Button from '@/shared/components/Button';
import { FaFileExcel } from 'react-icons/fa';
import messages from '../interactionMessages';
import InteractionOverviewFilters, { defaultFilters } from './InteractionOverviewFilters';
import SimplePanel from '@/shared/components/SimplePanel';
import { ColumnHeader, ScrollableTable } from '@/shared/components/table';
import InteractionOverviewRow from './InteractionOverviewRow';
import InteractionModals from './InteractionModals';
import SignalRService from '@/shared/services/SignalRService';
import Checkbox from '@/shared/components/input/Checkbox';

const filtersStorageKey = 'InteractionOverviewFilters';
class InteractionOverviewContainer extends Component {
  constructor(props) {
    super(props);

    const { filters: sessionFilters = {}, ...sessionParams } =
      JSON.parse(sessionStorage.getItem(filtersStorageKey)) || {};
    if (sessionFilters.startDate) sessionFilters.startDate = moment(sessionFilters.startDate);
    if (sessionFilters.endDate) sessionFilters.endDate = moment(sessionFilters.endDate);
    this.state = {
      interactions: [],
      allSelected: false,
      initialSearchParams: {
        sortKey: 'date',
        desc: true,
        interactionFilters: {
          ...defaultFilters,
          ...sessionFilters,
        },
        // Override with session storage params
        ...sessionParams,
        take: 50,
      },
    };
  }

  loadInteractions(searchParams = null) {
    const { intl } = this.props;

    const { interactionFilters, sortKey, desc } = searchParams;
    sessionStorage.setItem(
      filtersStorageKey,
      JSON.stringify({ filters: interactionFilters, sortKey, desc }),
    );

    if (typeof this.searchToken !== 'undefined') {
      this.searchToken.cancel('Operation canceled due to new request.');
    }
    this.searchToken = Axios.CancelToken.source();

    return api.base
      .post(
        `${config.appUrls.interactions}/api/overview`,
        {
          desc: searchParams.desc,
          sort: searchParams.sortKey,
          page: searchParams.page,
          take: searchParams.pageSize,
          ...interactionFilters,
        },
        {
          cancelToken: this.searchToken.token,
        },
      )
      .then(({ data }) => {
        const { allSelected } = this.state;
        const newInteractions = data.interactions.map(e => ({ ...e, selected: !!allSelected }));

        this.setState(state => ({
          hasLinkedRelations: data.hasLinkedRelations,
          interactions:
            searchParams.page > 0
              ? [...state.interactions, ...newInteractions]
              : [...newInteractions],
          searchParams: searchParams,
        }));
        return data.interactions;
      })
      .catch(handleHttpError(intl));
  }

  filtersChanged = filters => {
    this.setState(state => ({
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        interactionFilters: filters,
      },
    }));
  };

  onDeleteInteraction = () => {
    const { activeInteraction } = this.state;

    return api.base
      .delete(`${config.appUrls.interactions}/api/interactions/${activeInteraction.id}`)
      .then(() => {
        this.setState(state => ({
          modal: null,
          modalProps: {},
          activeInteraction: null,
          interactions: state.interactions.filter(x => x.id !== activeInteraction.id),
        }));
      });
  };

  sendInteractionViaEmail = async interaction => {
    const { intl } = this.props;
    const languageCode = intl.locale;
    try {
      const createPdfPromise = api.base.get(
        `${config.appUrls.interactions}/api/interactions/${interaction.id}/pdf/${
          languageCode.split('-')[0]
        }/save`,
      );
      const getRelationPromise = api.base.post(`${config.appUrls.api}/api/relation/Names`, [
        interaction.relationId,
      ]);

      const [{ data: pdf }, { data: relations }] = await Promise.all([
        createPdfPromise,
        getRelationPromise,
      ]);

      if (!pdf) {
        ToastrService.error(
          intl.formatMessage(messages.SomethingWentWrongWhileConvertingTheInteractionToPdf),
        );
        return;
      }
      let companyId = null;
      if (relations && relations.length > 0) companyId = relations[0].companyId;

      this.openModal('sendViaEmail', interaction, undefined, {
        companyId,
        attachments: [{ id: pdf.documentId, name: pdf.filename }],
      });
    } catch (e) {
      handleHttpError(intl)(e);
    }
  };

  openModal = (modal, interaction, onSave = null, modalProps = null) => {
    this.setState({
      modal: modal,
      modalProps: { ...(modalProps ?? {}), onSave: onSave ?? this.closeAllModals },
      activeInteraction: interaction,
    });
  };
  closeAllModals = () => this.setState({ modal: null, modalProps: {}, activeInteraction: null });

  updateInteractionAndCloseModals = updatedInteraction => {
    if (!updatedInteraction?.id) return;
    delete updatedInteraction.description; //Don't make everything slow with the html description...

    this.setState(({ interactions }) => ({
      interactions: interactions.map(x =>
        x.id === updatedInteraction.id ? { ...x, ...updatedInteraction } : x,
      ),
      modal: null,
      modalProps: {},
      activeInteraction: null,
    }));
  };
  getSelectionParams = () => {
    const { interactions, allSelected } = this.state;

    // Get all relation ids that don't match the select all checkbox
    const ids = interactions
      ? interactions.filter(x => x.selected !== allSelected).map(x => x.id)
      : [];
    return {
      ids,
      selectAll: allSelected,
    };
  };
  handleSelectAll = () => {
    const { interactions, allSelected } = this.state;

    this.setState(({ interaction, allSelected }) => {
      const isCurrentlySelected = !allSelected;
      return {
        allSelected: isCurrentlySelected,
        interactions: interactions.map(e => ({ ...e, selected: !!isCurrentlySelected })),
      };
    });
  };
  handleSelect = row => {
    this.setState(({ interactions }) => ({
      interactions: interactions.map(x => (x.id === row.id ? { ...x, selected: !x.selected } : x)),
    }));
  };

  createExcelReport = () => {
    const { intl } = this.props;
    this.setState({ creatingReport: true });
    const { initialSearchParams } = this.state;

    const languageCode = intl.locale;
    const params = {
      languageCode,
      signalRConnectionId: SignalRService.getConnectionId(),
      ...this.getSelectionParams(),
      filters: {
        desc: initialSearchParams.desc,
        sort: initialSearchParams.sortKey,
        ...(initialSearchParams.interactionFilters || {}),
      },
    };

    return api.base
      .post(`${config.appUrls.interactions}/api/overview/excel/`, params)
      .then(() => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
      })
      .catch(error => {
        handleHttpError(intl)(error);
      })
      .finally(() => {
        this.setState({ creatingReport: false });
      });
  };

  render() {
    const { intl } = this.props;
    const {
      creatingReport,
      loading,
      interactions,
      initialSearchParams,
      hasLinkedRelations,
      modal,
      modalProps,
      activeInteraction,
      allSelected,
    } = this.state;
    const hasData = interactions && interactions.length > 0;

    return (
      <div className="interaction-overview">
        <InteractionModals
          intl={intl}
          onClose={() => this.closeAllModals()}
          interaction={activeInteraction}
          type={modal}
          {...modalProps}
        />
        <SimplePanel
          article="21626756850332"
          title={intl.formatMessage(messages.InteractionOverview)}
        >
          <InteractionOverviewFilters
            filters={initialSearchParams.interactionFilters}
            onFilterChange={filters => this.filtersChanged(filters)}
            hasLinkedRelations={hasLinkedRelations}
            intl={intl}
          />
        </SimplePanel>
        <div className="row btn-row">
          <div className="col-md-6 pull-right content-right">
            <Button
              btnClassName="btn btn-xs btn-default margin10R"
              onClick={() => this.createExcelReport()}
              disabled={!hasData || creatingReport}
            >
              <span className="margin5R">
                <FaFileExcel className="file-icon" />
              </span>
              {intl.formatMessage(messages.ExportToExcel)}
            </Button>
          </div>
        </div>
        <ScrollableTable
          tableClassName="table-grid margin50B"
          loadItems={searchParams => this.loadInteractions(searchParams)}
          initialSearchParams={initialSearchParams}
          items={interactions}
          renderRow={row => (
            <InteractionOverviewRow
              key={row.id}
              interaction={row}
              intl={intl}
              onSelect={this.handleSelect}
              onEditInteraction={() =>
                this.openModal('editInteraction', row, this.updateInteractionAndCloseModals)
              }
              onOpenMail={() => this.openModal('openMail', row)}
              onSendViaEmail={() => this.sendInteractionViaEmail(row)}
              onShareItem={() => this.openModal('shareInteraction', row)}
              onDeleteInteraction={() =>
                this.openModal('deleteInteraction', row, this.onDeleteInteraction)
              }
            />
          )}
          noDataLabel={<FormattedMessage {...messages.NoInteractionsFound} />}
        >
          <ColumnHeader
            title={
              <Checkbox
                extraClass="margin2L"
                onClick={() => this.handleSelectAll()}
                value={allSelected}
              />
            }
            className="checkbox-column"
          />
          <ColumnHeader
            title={intl.formatMessage(messages.Type)}
            sortKey="type"
            className="col-xs"
          />
          <ColumnHeader
            title={intl.formatMessage(messages.Date)}
            sortKey="date"
            className="col-sm"
          />
          <ColumnHeader
            title={intl.formatMessage(messages.Relation)}
            sortKey="relation"
            className="col-sm"
          />
          <ColumnHeader title={intl.formatMessage(messages.Title)} sortKey="title" />
          <ColumnHeader title={intl.formatMessage(messages.CreatedBy)} className="col-md" />
          <ColumnHeader title="" className="actions-column hidden-xs col-fit" />
        </ScrollableTable>
      </div>
    );
  }
}

InteractionOverviewContainer.propTypes = {
  intl: intlShape.isRequired,
};

export default injectIntl(InteractionOverviewContainer);
